import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { DropdownField, Text } from '@frontend/design-system';
import { inactiveEntityOptionStyles } from '../../../../components/hidden-entity-option-label';
import type { EntityListItem } from '../../../../types';
import { HiddenEntityOptionLabel } from '../../../hidden-entity-option-label';
import { AvailabilityFormRow } from '../../availability-form/atoms/availability-form-row';
import { AvailabilityFormSection } from '../../availability-form/atoms/availability-form-section';
import { AvailabilityMode, GetAvailabilityRuleFormFieldProps } from '../../availability-form/types';

type ProviderFormProps = {
  providerList: EntityListItem[];
  operatoryList: EntityListItem[];
  getFieldProps: GetAvailabilityRuleFormFieldProps;
  availabilityMode: AvailabilityMode;
};

export const ProviderForm = ({ providerList, operatoryList, getFieldProps, availabilityMode }: ProviderFormProps) => {
  const { t } = useTranslation('schedule-settings');

  return (
    <AvailabilityFormSection title={t('Provider')}>
      <div css={styles.stack}>
        {availabilityMode === 'PROVIDER' ? (
          <AvailabilityFormRow>
            <DropdownField label={t('Operatory')} {...getFieldProps('operatory')} hasNonFloatingLabel>
              <DropdownField.Option key={'empty'} value={''}>
                {t('Select an operatory')}
              </DropdownField.Option>
              {operatoryList.map(({ label, value, isHidden, isArchived }) => {
                const isInactive = isHidden || isArchived;
                return (
                  <DropdownField.Option
                    key={value}
                    value={value}
                    disabled={isInactive}
                    className={isInactive ? inactiveEntityOptionStyles : undefined}
                  >
                    {isInactive ? (
                      <HiddenEntityOptionLabel
                        isArchived={isArchived}
                        label={label}
                        tooltipMessage={
                          isArchived
                            ? t('This operatory has been archived')
                            : t('This operatory is hidden from the calendar')
                        }
                      />
                    ) : (
                      label
                    )}
                  </DropdownField.Option>
                );
              })}
            </DropdownField>
          </AvailabilityFormRow>
        ) : (
          <>
            <AvailabilityFormRow>
              <div>
                <DropdownField
                  label={t('Serviceable Provider')}
                  {...getFieldProps('serviceableProvider')}
                  hasNonFloatingLabel
                >
                  <DropdownField.Option key={'empty'} value={''}>
                    {t('Select a provider')}
                  </DropdownField.Option>
                  {providerList.map(({ label, value, isHidden, isArchived }) => {
                    const isInactive = isHidden || isArchived;
                    return (
                      <DropdownField.Option
                        key={value}
                        value={value}
                        disabled={isInactive}
                        className={isInactive ? inactiveEntityOptionStyles : undefined}
                      >
                        {isInactive ? (
                          <HiddenEntityOptionLabel
                            isArchived={isArchived}
                            label={label}
                            tooltipMessage={
                              isArchived
                                ? t('This provider has been archived')
                                : t('This provider is hidden from the calendar')
                            }
                          />
                        ) : (
                          label
                        )}
                      </DropdownField.Option>
                    );
                  })}
                </DropdownField>
                <Text size='small' color='subdued' css={styles.hint}>
                  {t('The provider providing the service')}
                </Text>
              </div>
            </AvailabilityFormRow>
          </>
        )}
        <AvailabilityFormRow>
          <div>
            <DropdownField label={t('Primary Provider')} {...getFieldProps('billableProvider')} hasNonFloatingLabel>
              <DropdownField.Option key={'empty'} value={''}>
                {t('Select a provider')}
              </DropdownField.Option>
              {providerList?.map(({ label, value, isHidden, isArchived }) => {
                const isInactive = isHidden || isArchived;
                return (
                  <DropdownField.Option
                    key={value}
                    value={value}
                    disabled={isInactive}
                    className={isInactive ? inactiveEntityOptionStyles : undefined}
                  >
                    {isInactive ? (
                      <HiddenEntityOptionLabel
                        isArchived={isArchived}
                        label={label}
                        tooltipMessage={
                          isArchived
                            ? t('This provider has been archived')
                            : t('This provider is hidden from the calendar')
                        }
                      />
                    ) : (
                      label
                    )}
                  </DropdownField.Option>
                );
              })}
            </DropdownField>
            <Text size='small' color='subdued' css={styles.hint}>
              {t('The provider that will be billable')}
            </Text>
          </div>
        </AvailabilityFormRow>
      </div>
    </AvailabilityFormSection>
  );
};

const styles = {
  stack: css`
    display: flex;
    flex-direction: column;
  `,
  hint: css`
    margin-top: var(--fabric-space-gap-xxs);
  `,
};
